import React from 'react';
import { Box } from '@mui/material';
import PropTypes from 'prop-types';
import { PALETTE, BORDERS, SHADOWS } from '../theme';

/**
 * Layout container used throughout the app.
 *
 * Default (`variant="plain"`): a quiet, chromeless container that just owns
 * padding and a background. Use this for grouped content that doesn't need a
 * visible frame — it stops the page from looking like card soup on a busy
 * gradient.
 *
 * `variant="frame"`: a real card with subtle border and shadow. Use this only
 * for content that genuinely needs visual containment (the map, the checklist
 * accordion) — these elements feel "wrong" without a frame around them.
 */
const GlassCard = ({ children, sx = {}, className, variant = 'plain', ...props }) => {
    const baseStyles =
        variant === 'frame'
            ? {
                  backgroundColor: PALETTE.surface,
                  border: BORDERS.faint,
                  borderRadius: { xs: 2.5, sm: 3 },
                  boxShadow: { xs: 'none', sm: SHADOWS.card }
              }
            : {
                  // Plain — invisible container, just owns padding.
                  backgroundColor: 'transparent'
              };

    return (
        <Box
            sx={{
                padding: variant === 'frame' ? { xs: 1.5, sm: 3 } : { xs: 0, sm: 0 },
                ...baseStyles,
                ...sx
            }}
            className={className}
            {...props}
        >
            {children}
        </Box>
    );
};

GlassCard.propTypes = {
    children: PropTypes.node,
    sx: PropTypes.object,
    className: PropTypes.string,
    variant: PropTypes.oneOf(['plain', 'frame'])
};

export default GlassCard;
